import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const CHAPTERS = [
    {
        n: "01",
        title: "Our Philosophy",
        body: "Simplicity is one of life's greatest luxuries. Every day, we make thousands of decisions. Most of them don't matter. Choosing your socks shouldn't be one of them. No endless collections. No seasonal colors. No unnecessary choices. Just timeless essentials designed to simplify your life.",
    },
    {
        n: "02",
        title: "Our Concept",
        body: "Tony Rays doesn't sell socks. Tony Rays resets your drawer. One premium box. One decision. Months of simplicity. Every pair is designed to be identical, timeless and permanently replaceable. Lose one. Take another. No sorting. No matching. No wasted pairs.",
    },
    {
        n: "03",
        title: "Our Collection",
        body: "Simple products. Simple choices. Simple life. Tony Rays offers only two boxes. Nothing more. Nothing less.",
    },
    {
        n: "04",
        title: "Our Promise",
        body: "When you find the style you love, it will still be available tomorrow, next year, and years from now. No discontinued collections. No changing designs. No surprises. Just reorder the exact same box, again and again.",
    },
    {
        n: "05",
        title: "Our Manifesto",
        body: "The world doesn't need more products. It needs better ones. Less clutter. Less noise. Less decisions. More freedom. Tony Rays exists to simplify one small part of your everyday life. One box. One drawer. One decision.",
    },
    {
        n: "06",
        title: "Our Vision",
        body: "Today, Tony Rays begins with socks. Tomorrow, it becomes the home of everyday essentials — boxers, T-shirts, underwear. The same philosophy. The same simplicity. The same premium quality. Fewer choices. More freedom.",
    },
];

const WHY = [
    "Timeless design",
    "Premium quality",
    "Permanent collection",
    "No mismatched socks",
    "No wasted pairs",
    "No unnecessary decisions",
    "Minimalist by design",
    "Built to last",
];

const FAQ = [
    {
        q: "Why only a few models?",
        a: "Because simplicity is our mission. The fewer decisions you make, the easier life becomes.",
    },
    {
        q: "Will future orders be exactly the same?",
        a: "Yes. That's our promise — the exact same product, years from now.",
    },
    {
        q: "Why only black and white?",
        a: "Because timeless never goes out of style, and they match everything.",
    },
    {
        q: "What's the difference between the two boxes?",
        a: "The One Box is 20 identical pairs of one model and color. The Starter Box is 20 assorted pairs across all three heights, so you can find your favorite.",
    },
];

function Unit({ progress, index }) {
    const start = index / 24;
    const opacity = useTransform(progress, [start, start + 0.15], [0.12, 1]);
    return (
        <motion.div
            style={{ opacity }}
            className="aspect-square bg-ink flex items-center justify-center"
        >
            <span className="font-serif text-paper text-[0.6rem] md:text-xs">
                TR
            </span>
        </motion.div>
    );
}

function WhyTwenty() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 0.8", "end 0.4"],
    });
    return (
        <div ref={ref} className="grid grid-cols-5 sm:grid-cols-10 gap-2">
            {Array.from({ length: 20 }).map((_, i) => (
                <Unit key={i} progress={scrollYProgress} index={i} />
            ))}
        </div>
    );
}

export default function Philosophy() {
    return (
        <div data-testid="philosophy-page" className="pt-28 md:pt-40">
            <section className="max-w-[1500px] mx-auto px-5 sm:px-8">
                <Reveal>
                    <span className="eyebrow text-ink/50">Our Philosophy</span>
                    <h1 className="mt-6 font-serif font-light text-[13vw] sm:text-[10vw] md:text-8xl leading-[0.9] tracking-[-0.04em] max-w-4xl">
                        Simplicity is one of life's greatest luxuries.
                    </h1>
                </Reveal>
            </section>

            {/* Chapters */}
            <section className="max-w-[1100px] mx-auto px-5 sm:px-8 mt-24 md:mt-36">
                {CHAPTERS.map((c, i) => (
                    <Reveal key={c.n} delay={0.05}>
                        <div className="grid md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-line">
                            <div className="md:col-span-3">
                                <span className="font-serif text-5xl text-ink/15">
                                    {c.n}
                                </span>
                                <h2 className="mt-3 font-serif text-2xl tracking-[-0.02em]">
                                    {c.title}
                                </h2>
                            </div>
                            <p className="md:col-span-9 text-lg md:text-xl leading-relaxed text-ink/75">
                                {c.body}
                            </p>
                        </div>
                    </Reveal>
                ))}
            </section>

            {/* Why 20 */}
            <section className="border-y border-line mt-20">
                <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32 grid md:grid-cols-2 gap-16 items-center">
                    <Reveal>
                        <span className="eyebrow text-ink/50">Why 20 Pairs</span>
                        <h2 className="mt-5 font-serif text-4xl md:text-5xl tracking-[-0.03em]">
                            Because you're not buying socks.
                        </h2>
                        <p className="mt-6 text-ink/70 leading-relaxed">
                            You're replacing your entire drawer. One purchase. One
                            decision. Months of freedom. You gain time, space and
                            mental energy.
                        </p>
                    </Reveal>
                    <WhyTwenty />
                </div>
            </section>

            {/* Why Tony Rays checklist */}
            <section className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
                <Reveal>
                    <span className="eyebrow text-ink/50">Why Tony Rays</span>
                </Reveal>
                <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line">
                    {WHY.map((w, i) => (
                        <Reveal key={w} delay={i * 0.04}>
                            <div className="bg-paper p-6 md:p-8 h-full">
                                <span className="font-serif text-lg leading-tight block">
                                    {w}
                                </span>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <Marquee text="One decision · Months of simplicity" separator="·" />

            {/* FAQ */}
            <section className="max-w-[900px] mx-auto px-5 sm:px-8 py-24 md:py-32">
                <Reveal>
                    <h2 className="font-serif text-4xl md:text-5xl tracking-[-0.03em]">
                        Questions
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <Accordion type="single" collapsible className="mt-10" data-testid="faq">
                        {FAQ.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`item-${i}`}
                                className="border-line"
                            >
                                <AccordionTrigger className="font-serif text-xl md:text-2xl text-left tracking-[-0.02em] hover:no-underline py-6">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-base text-ink/70 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </Reveal>
            </section>

            {/* CONTACT — one clear entry point */}
            <section
                className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24 md:py-32 border-t border-line"
                data-testid="contact-section"
            >
                <div className="grid md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <Reveal>
                            <span className="eyebrow text-ink/50">Get in touch</span>
                        </Reveal>
                        <Reveal delay={0.05}>
                            <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-[-0.03em] leading-[0.95]">
                                One question. <br />
                                <span className="italic font-light">One reply.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 max-w-md text-base md:text-lg text-ink/70 leading-relaxed">
                                Questions on sizing, wholesale, press inquiries or partnerships — write to us directly.
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-5">
                        <Reveal delay={0.15}>
                            <a
                                href="mailto:contact@tonyrays.com"
                                data-testid="philosophy-contact-email"
                                className="group inline-flex items-baseline gap-3 border-b border-ink/30 pb-3 hover:border-ink transition-colors"
                            >
                                <span className="font-serif text-3xl md:text-4xl tracking-[-0.02em] text-ink">
                                    contact@tonyrays.com
                                </span>
                                <span className="text-ink/50 text-sm transition-transform duration-300 group-hover:translate-x-1">→</span>
                            </a>
                            <p className="mt-4 text-xs uppercase tracking-[0.24em] text-ink/45">
                                Response within 48 hours
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
